import React, { useEffect, useState } from 'react';
import { initializeApp } from 'firebase/app';
import { getAuth, signOut, onAuthStateChanged } from 'firebase/auth';
import { getFunctions } from 'firebase/functions';
import BackRoom from './components/BackRoom';
import LoginPage from './components/LoginPage';
import { INTENTIONAL_SIGNOUT_KEY, isAllowedEmail, useAuthGuard } from './auth/AuthGuard';

const firebaseConfig = {
  apiKey:            process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain:        process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId:         process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket:     process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId:             process.env.REACT_APP_FIREBASE_APP_ID,
};

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
getFunctions(app);
const BACK_ROOM_OWNER_UID = 'f5OF2EhyX2aHzyJmfUISwU9jQng2';

// ============================================================
// ============================================================
// APP ROOT
// Intentional sign-out users see the in-app login screen.
// ============================================================
export default function App() {
  const [user, setUser] = useState(undefined);

  // Canonical host guard:
  // Some origins are blocked by Firebase API key referer policy for auth calls.
  // Always route those origins to the canonical web.app host before sign-in attempts.
  useEffect(() => {
    const blockedHosts = new Set([
      'assistant.ramdesignworks.com',
      'ramdesignworks-exec-staff.firebaseapp.com',
    ]);
    const canonicalOrigin = 'https://ramdesignworks-exec-staff.web.app';
    if (!blockedHosts.has(window.location.hostname)) return;

    const target = `${canonicalOrigin}${window.location.pathname}${window.location.search}${window.location.hash}`;
    window.location.replace(target);
  }, []);

  // Auth state — sets user; allowlist + redirect handled by useAuthGuard below.
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, u => setUser(u || null));
    return unsub;
  }, []);

  // Auth guard — non-allowlisted signed-in users go to /purgatory.html.
  useAuthGuard(user);

  async function handleSignOut() {
    sessionStorage.setItem(INTENTIONAL_SIGNOUT_KEY, '1');
    await signOut(auth);
  }

  // ---- LOADING ----
  if (user === undefined) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        height: '100dvh',
        backgroundColor: '#060d18', color: '#4a5568',
        fontFamily: "'Inter', sans-serif", fontSize: '14px',
      }}>
        Loading…
      </div>
    );
  }

  // ---- LOGGED OUT (intentional sign-out path) ----
  if (!user) return <LoginPage />;

  const isBackRoomOwner = !!user?.uid && isAllowedEmail(user?.email) && user.uid === BACK_ROOM_OWNER_UID;

  if (!isBackRoomOwner) {
    window.location.replace('/purgatory.html');
    return null;
  }

  // ---- LOGGED IN ----
  return (
    <div style={{ position: 'relative', height: '100dvh' }}>
      <BackRoom onClose={handleSignOut} fullPage />

      {/* PWA install banner — disabled, already installed */}
    </div>
  );
}
